import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useNetSuiteConfigs from "@/hooks/useNetSuiteConfigs";
import SearchBar from "@/components/SearchBar";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { IconCheck } from "@velaro/velaro-component-lib/";
import NetsuitePanel from "./NetSuitePanel";
import { metaDataOptions } from "./MetaDataOptions";

const handleSearch = (searchText: string) => {};

export interface NetSuiteMap {
  id: number;
  netSuiteField: any;
  label: string;
  linkedTo: string;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  control: string;
  defaultValue: string;
  recordType: string;
}

interface Props {
  teamId: number;
}

export default function NetSuiteDataMapping(props: Props) {
  const [panels, setPanels] = useState<{ id: number; metadataType: string }[]>(
    []
  );
  const [showSaved, setShowSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mappedFields, setMappedFields] = useState<any[]>([]);
  const api = useApi();
  const [selectedConfig, setSelectedConfig] = useState<number | undefined>(
    undefined
  );
  const { items, loading: configLoading } = useNetSuiteConfigs(props.teamId);

  const addPanel = () =>
    setPanels([...panels, { id: panels.length + 1, metadataType: "Manual" }]);

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  useEffect(() => {
    if (!configLoading && items.length > 0) {
      setSelectedConfig(items[0]?.id);
    }
  }, [items, configLoading]);

  const fetchMappingFieldCount = useCallback(
    async (
      metadataType: string
    ): Promise<{ metadataType: string; dataLength: number }> => {
      if (selectedConfig == 0) return { metadataType, dataLength: 0 };

      const resp = await api.messaging.get(
        `NetSuite/Integrations/Mapping/${metadataType}/${selectedConfig}`
      );
      const json = await resp.json();

      return { metadataType, dataLength: json.length };
    },
    [api.messaging, selectedConfig]
  );

  const fetchAllMappings = async () => {
    setLoading(true);
    let totalPanels = 0;
    let panelsToAdd: { id: number; metadataType: string }[] = [];

    for (const option of metaDataOptions) {
      if (option !== "") {
        const { metadataType, dataLength } = await fetchMappingFieldCount(
          option
        );
        if (dataLength > 0) {
          panelsToAdd.push({ id: totalPanels + 1, metadataType });
          totalPanels += 1;
        }
      }
    }

    setPanels(panelsToAdd);
    setLoading(false);
    console.log("Total Panels Added:", totalPanels);
  };

  // Fetch mappings when selectedConfig is set
  useEffect(() => {
    if (selectedConfig != undefined && selectedConfig > 0) {
      fetchAllMappings(); // Trigger fetching for all metadata options
    }
  }, [selectedConfig]);

  useEffect(() => {
    showSavedMessage();
  }, []);

  return (
    <div>
      <div className="justify-between items-center inline-flex w-full">
        <div className="justify-start items-start flex">
          <SearchBar placeholderText="Search" onSearch={handleSearch} />
        </div>

        <div className="justify-end items-center gap-3 flex w-full">
          {showSaved && (
            <div className="flex items-center gap-2 text-slate-500">
              <span className="filter invert">
                {React.createElement(IconCheck, {
                  className: "text-slate-500"
                })}
              </span>
              <div className="text-slate-500 text-sm font-medium">
                All changes saved
              </div>
            </div>
          )}
          <PrimaryBlueButton
            label="Add Mapping"
            onClick={addPanel}
            className=""
            prefixIcon={React.createElement(IconPlus)}
          />
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center my-4">
          <span>Loading mappings...</span>
        </div>
      )}

      <div className="mt-4">
        {panels.map((panel) => (
          <NetsuitePanel
            key={panel.id}
            teamId={props.teamId!}
            metadataType={panel.metadataType}
            showSavedMessage={showSavedMessage}
          />
        ))}
      </div>
    </div>
  );
}
